import { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, Stack, Button, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { UpdateMerchant } from '../../../network/Merchants';
import { APP_CONSTANTS } from '../../../URLConstants';
import { parseValidationErrorMessage } from '../../../utils/Utilities';

const style = {
  position: 'absolute',
  width: '100%',
  maxWidth: { sm: '90%', xs: '95%', md: 500 },
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '70%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

const UpdateMerchantSchema = Yup.object().shape({
  address: Yup.string().required('Required'),
  pincode: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  name: Yup.string().required('Required'),
  phone: Yup.string()
    .required('Required')
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits'),
  state: Yup.string().required('Required')
});

export default function UpdateMerchantModal({ open, handleClose, merchantData, onUpdate }) {
  const formik = useFormik({
    initialValues: {
      address: merchantData?.merchant?.deliveryAddress || '',
      pincode: merchantData?.merchant?.deliveryPincode || '',
      city: merchantData?.merchant?.deliveryCity || '',
      name: merchantData?.merchant?.name || '',
      phone: merchantData?.merchant?.phone || '',
      state: merchantData?.merchant?.deliveryState || ''
    },
    enableReinitialize: true,
    validationSchema: UpdateMerchantSchema,
    onSubmit: (values, { setSubmitting }) => {

      UpdateMerchant(merchantData?.merchant?.id, values)
        .then((res) => {
          onUpdate(APP_CONSTANTS?.MERCHANT_UPDATE_SUCCESSFULL, 'Merchant has been updated!');
        })
        .catch((err) => {
          onUpdate(APP_CONSTANTS?.MERCHANT_UPDATE_UNSUCCESSFULL, parseValidationErrorMessage(err));
        });
      setSubmitting(false);
      handleClose();
    }
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleBlur,
    handleChange,
    getFieldProps
  } = formik;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="update-merchant-modal-title"
      aria-describedby="update-merchant-modal-description"
      data-testid="update_merchant_modal"
    >
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box
            sx={{
              ...style,
              maxHeight: '90vh',
              width: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat', mb: 2 }}>
              Update Merchant
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                {...getFieldProps('name')}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                fullWidth
              />
              <TextField
                label="Phone"
                {...getFieldProps('phone')}
                error={Boolean(touched.phone && errors.phone)}
                helperText={touched.phone && errors.phone}
                fullWidth
              />
              <TextField
                label="Address"
                {...getFieldProps('address')}
                error={Boolean(touched.address && errors.address)}
                helperText={touched.address && errors.address}
                fullWidth
                multiline
                maxRows={3}
              />
              <TextField
                label="City"
                {...getFieldProps('city')}
                error={Boolean(touched.city && errors.city)}
                helperText={touched.city && errors.city}
                fullWidth
              />
              <TextField
                label="State"
                {...getFieldProps('state')}
                error={Boolean(touched.state && errors.state)}
                helperText={touched.state && errors.state}
                fullWidth
              />
              <TextField
                label="Pincode"
                {...getFieldProps('pincode')}
                error={Boolean(touched.pincode && errors.pincode)}
                helperText={touched.pincode && errors.pincode}
                fullWidth
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                data-testid="update_merchant_close_button"
                color="error"
                onClick={() => {
                  handleClose();
                  formik.resetForm();
                }}
              >
                Cancel
              </Button>
              <LoadingButton loading={isSubmitting} variant="contained" color="info" type="submit">
                Save
              </LoadingButton>
            </Stack>
          </Box>
        </Form>
      </FormikProvider>
    </Modal>
  );
}

UpdateMerchantModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  merchantData: PropTypes.object,
  onUpdate: PropTypes.func.isRequired
};
